import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { addResourceNameToQueryResult, useAxiosInstance } from './utils';
import type { ApiError } from './utils/types';

const BalanceValueSchema = z.preprocess(val => Number(val), z.number());

const BalanceSchema = z.object({
  '0': BalanceValueSchema,
  '30': BalanceValueSchema,
  '60': BalanceValueSchema,
  '90': BalanceValueSchema,
  notDue: BalanceValueSchema,
});

const AgedBalanceOverTimeSchema = z.object({
  computedAt: z.string(),
  isComputing: z.boolean(),
  data: z.record(
    z.string(),
    z.object({
      credits: BalanceSchema,
      debits: BalanceSchema,
    }),
  ),
});

export type AgedBalanceOverTime = z.infer<typeof AgedBalanceOverTimeSchema>;

type LoadAgedBalanceOverTimeVaraibles = {
  viewId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export async function loadAgedBalanceOverTimeFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
): Promise<AgedBalanceOverTime> {
  const instance = await axiosInstance;

  const { data } = await instance.get('/analytics/aged_balances/stacked', {
    params: {
      view_id: queryKey[2].viewId,
      starts_at: queryKey[2].startDate,
      ends_at: queryKey[2].endDate,
    },
  });

  return AgedBalanceOverTimeSchema.parse(data);
}

export function useLoadAgedBalanceOverTime(variables: LoadAgedBalanceOverTimeVaraibles) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<AgedBalanceOverTime, ApiError>({
    queryKey: ['aged-balance', 'over-time', variables],
    queryFn: context => loadAgedBalanceOverTimeFn(axiosInstance, context),
    placeholderData: previousData => previousData,
  });

  return addResourceNameToQueryResult<AgedBalanceOverTime, ApiError, 'agedBalanceOverTime'>(
    'agedBalanceOverTime',
    queryResult,
  );
}
